import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors } from '../../constants/colors';
import { Ionicons } from '@expo/vector-icons';
import { firebaseService } from '../../services/FirebaseService';
import { useNavigation, useRoute } from '@react-navigation/native';
import { storeAccountCreationTimestamp } from '../../services/announcementNotifications';

export default function VerificationScreen() {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [sendingEmail, setSendingEmail] = useState(false);
  const navigation = useNavigation();
  const route = useRoute();
  const { email, password, username } = route.params as { 
    email: string;
    password: string;
    username: string;
  };

  useEffect(() => {
    // Send initial verification email
    sendVerificationEmail();

    // Start countdown timer
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const sendVerificationEmail = async () => {
    if (sendingEmail) return;
    setSendingEmail(true);
    try {
      await firebaseService.sendVerificationCode(email);
      Alert.alert(
        'Verification Code Sent',
        'Please check your email for the verification code. If you don\'t see it, check your spam folder.'
      );
    } catch (error: any) {
      Alert.alert(
        'Error Sending Code',
        'Failed to send verification code. Please try again or contact support if the problem persists.'
      );
      console.error('Error details:', error);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleResendCode = () => {
    if (timer > 0) return;
    sendVerificationEmail();
    setTimer(60);
  };

  const handleVerify = async () => {
    if (loading) return;

    if (!verificationCode) {
      Alert.alert('Error', 'Please enter the verification code');
      return;
    }

    if (verificationCode.length !== 6) {
      Alert.alert('Error', 'Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(true);

    try {
      // Verify the code
      const isValid = await firebaseService.verifyCode(email, verificationCode);
      
      if (isValid) {
        // If verified, create the account
        const userCredential = await firebaseService.signUp(email, password, username);
        if (userCredential.user) {
          await storeAccountCreationTimestamp();
          Alert.alert(
            'Welcome to NUST Life!',
            'Your account has been created successfully. You can now access all features of the app.'
          );
        }
      } else {
        Alert.alert(
          'Invalid Code',
          'The verification code is invalid or has expired. Please try again or request a new code.'
        );
        setVerificationCode('');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      Alert.alert(
        'Verification Failed',
        'There was a problem verifying your code. Please try again or contact support if the problem persists.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.content}>
            <Ionicons 
              name="mail-outline" 
              size={80} 
              color={colors.primary}
              style={styles.icon}
            />
            
            <Text style={styles.title}>Verify Your Email</Text>
            <Text style={styles.subtitle}>
              We've sent a verification code to:
            </Text>
            <Text style={styles.email}>{email}</Text>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  placeholder="Enter verification code"
                  value={verificationCode}
                  onChangeText={setVerificationCode}
                  keyboardType="number-pad"
                  maxLength={6}
                  placeholderTextColor={colors.textLight}
                />
              </View>

              <TouchableOpacity
                style={[styles.button, loading && styles.buttonDisabled]}
                onPress={handleVerify}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color={colors.white} />
                ) : (
                  <Text style={styles.buttonText}>Verify</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.resendButton,
                  timer > 0 && styles.resendButtonDisabled
                ]}
                onPress={handleResendCode}
                disabled={timer > 0 || sendingEmail}
              >
                {sendingEmail ? (
                  <ActivityIndicator color={colors.primary} size="small" />
                ) : (
                  <Text style={[
                    styles.resendText,
                    timer > 0 && styles.resendTextDisabled
                  ]}>
                    {timer > 0 
                      ? `Resend code in ${timer}s`
                      : 'Resend code'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 5,
    textAlign: 'center',
  },
  email: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '600',
    marginBottom: 30,
  },
  form: {
    width: '100%',
    maxWidth: 400,
  },
  inputContainer: {
    marginBottom: 20,
  },
  input: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 15,
    fontSize: 16,
    color: colors.text,
    textAlign: 'center',
    letterSpacing: 8,
  },
  button: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  resendButton: {
    padding: 10,
    alignItems: 'center',
  },
  resendButtonDisabled: {
    opacity: 0.7,
  },
  resendText: {
    color: colors.primary,
    fontSize: 14,
  },
  resendTextDisabled: {
    color: colors.textSecondary,
  },
});
